import { useEffect, useRef } from 'react';
import { syncSentryUser } from '@/lib/sentry-user';
import { fetchCurrentUser, getCurrentPerson, clearCurrentPerson, setCurrentUser, getCurrentUser, AuthExpiredError, UserNotFoundError } from '@/db/services/user_service';
import { removeItem } from '@/db/services/application_session_service';
import { CURRENT_USER_ID_KEY, logout, registerDeviceIfNeeded, refreshDeviceIfStale, softSignOut, trackDeviceRegisteredEvent } from '@/lib/auth';
import { createLogger, LogLevel } from '../lib/logger';

const logger = createLogger('SessionValidator', LogLevel.INFO);

/**
 * Hook that validates the server session still matches local state.
 *
 * Runs when:
 * - The tab becomes visible (user switches back from another tab)
 * - The browser comes back online
 *
 * Detects:
 * - Session cookie changed (different user logged in on the online app)
 * - Session expired (401 from server)
 * - Stored person no longer valid for current user
 *
 * On mismatch: clears local auth state and redirects to login or org selection.
 */
export function useSessionValidator() {
  // Debounce: don't validate more than once per 30 seconds
  const lastCheckRef = useRef(0);
  const checkingRef = useRef(false);

  useEffect(() => {
    // Rehydrate the Sentry user from local storage on mount. setSessionEstablished
    // only runs once after the auth callback — without this, page reloads and
    // cold starts would emit unattributed events until the next login. While
    // the screen is locked the records are unreadable; the unlock path syncs
    // again as soon as the key is available.
    void syncSentryUser();

    const validate = async () => {
      // Skip if offline — can't validate
      if (!navigator.onLine) return;

      // Skip if there's no local user to validate against. The hook detects
      // drift between local and server state; with no local state, a 401
      // from /current_user is the expected logged-out response, not an
      // expired session. Treating it as expiry on /login produces a
      // redirect-to-/login → reload → revalidate loop.
      if (!(await getCurrentUser())) return;

      // Debounce
      const now = Date.now();
      if (now - lastCheckRef.current < 30_000) return;
      if (checkingRef.current) return;

      checkingRef.current = true;
      lastCheckRef.current = now;

      try {
        // Transition: if this install still has a cookie-only session but no
        // bearer token yet, register one before doing anything else. The
        // helper is a no-op when a token already exists.
        const registeredDevice = await registerDeviceIfNeeded();

        // Silent 14-day refresh — extends the bearer's expires_at by another
        // 30 days while keeping the same token. No-op when the bearer is
        // newer than 14 days, when offline, or when the server doesn't yet
        // support /refresh. Failures don't block the rest of validate().
        await refreshDeviceIfStale();

        // Fetch the actual current user from the server (bearer or cookie,
        // chosen by fetchCurrentUser based on whether a device token has
        // been registered).
        const serverUser = await fetchCurrentUser();

        // Emit device_registered analytics for the transition path, now that
        // we have a serverUser to fall back to. The validator's only entry
        // guard is `current_user_id` (line 52), which is set independently
        // of `current_person`: a multi-person user mid-selection has the
        // first but not the second. Without a personOverride trackEvent
        // would drop the event in that window — the same bug the rest of
        // this PR was written to fix.
        if (registeredDevice) {
          const storedPerson = await getCurrentPerson();
          const attributablePerson = storedPerson ?? serverUser.people?.[0];
          trackDeviceRegisteredEvent(
            registeredDevice,
            attributablePerson
              ? { personId: attributablePerson.id, organizationId: attributablePerson.organization.id }
              : undefined,
          );
        }

        // Compare with stored user
        const storedUser = await getCurrentUser();
        if (storedUser && storedUser.id !== serverUser.id) {
          // Different user is now logged in on the server!
          logger.info('Server session user changed', {
            storedUserId: storedUser.id,
            serverUserId: serverUser.id,
          });

          // Clear all local auth state
          await removeItem(CURRENT_USER_ID_KEY);
          await clearCurrentPerson();

          // Store new user and redirect to org selection
          await setCurrentUser(serverUser);
          await syncSentryUser();
          window.location.href = '/select-organization';
          return;
        }

        // Same user — update stored user data (people array may have changed)
        await setCurrentUser(serverUser);
        // Also the periodic chance to attribute events when the mount-time
        // sync ran against a locked database.
        await syncSentryUser();

        // Validate that stored person still belongs to this user
        const storedPerson = await getCurrentPerson();
        if (storedPerson) {
          const personStillValid = serverUser.people?.some(
            (p) => p.id === storedPerson.id,
          );
          if (!personStillValid) {
            logger.info('Stored person no longer valid for current user', {
              storedPersonId: storedPerson.id,
              availablePeople: serverUser.people?.map((p) => p.id),
            });
            await clearCurrentPerson();
            // useCurrentPerson() will reactively update → AuthWrapper will redirect
          }
        }
      } catch (error) {
        if (error instanceof UserNotFoundError) {
          // Mon Ami itself says the user no longer exists (its record_not_found
          // body; any other 404 never reaches here) — local state is corrupt.
          // Full logout (DB wipe + cache clear) because the user record no longer
          // exists on the server, so all local data is orphaned and unrecoverable.
          logger.error('Current user not found on server — forcing logout to re-sync', error);
          await logout();
          return;
        }
        if (error instanceof AuthExpiredError) {
          // 401/403 — session expired. softSignOut clears identifiers + the
          // device token + Sentry attribution and redirects to /login, all
          // while preserving the encrypted DB. We funnel through it rather
          // than inlining the cleanup so this branch can't drift out of
          // sync with the contract documented at the top of auth.ts.
          logger.info('Session expired — clearing local state', error);
          await softSignOut();
          return;
        }
        // Network errors are expected when offline — ignore
        logger.debug('Session validation failed (may be expected):', error);
      } finally {
        checkingRef.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        validate();
      }
    };

    const handleOnline = () => {
      validate();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    // Also validate on mount (covers fresh page loads where session may be stale)
    validate();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, []);
}
